import './SignInStyles.scss';
import { ErrorMessage } from "@hookform/error-message";
import { FC, useContext, useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { routes } from '../../../routes/RoutesComponent';
import ModalsContext from '../../../../domain/providers/modal/ModalsContext';
import ModalsContextType from '../../../../domain/providers/modal/ModalsContextType';
import LocalizationContext from '../../../../domain/providers/localization/LocalizationContext';
import LocalizationContextType from '../../../../domain/providers/localization/LocalizationContextType';
import KeyWordLocalization from '../../../providers/localization/dictionaries/KeyWordLocalization';
import Validators from '../../../utils/Validators';
import di from '../../../../di/DependencyInjection';
import SignInUseCase, { SignInUseCaseName } from '../../../../domain/use_cases/auth/SignInUseCase';
import { isRight } from 'fp-ts/lib/Either';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import SocialLoginComponent from '../../../components/socialLogin/SocialLoginComponent';

import disclamerImage from "../../../assets/disclaimer.png";

const LOCAL_STORAGE_MODAL_KEY = 'modal_accepted';

const SignInPage: FC<{}> = () => {
    const { register, handleSubmit, formState: { errors } } = useForm();
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { addToast } = useContext(ModalsContext) as ModalsContextType;

    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [pendingData, setPendingData] = useState<any | null>(null);

    const hasAcceptedModal = useRef<boolean>(false);
    const formRef = useRef<HTMLFormElement>(null);

    useEffect(() => {
        hasAcceptedModal.current = false;
    }, []);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            console.log(e.key);
            
            if (e.key === 'Enter') {
                const activeElement = document.activeElement as HTMLElement;
                if (formRef.current && formRef.current.contains(activeElement)) {
                    if (!hasAcceptedModal.current) {
                        e.preventDefault();
                        const formData = new FormData(formRef.current);
                        const data = Object.fromEntries(formData.entries());
                        setPendingData(data);
                        setShowModal(true);
                    }
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleModalAccept = async () => {
        localStorage.setItem(LOCAL_STORAGE_MODAL_KEY, 'true');
        setShowModal(false);
        hasAcceptedModal.current = true;

        if (pendingData) {
            await proceedWithLogin(pendingData);
            setPendingData(null);
        }
    };

    const onSubmit = async (data: any) => {
        if (!hasAcceptedModal.current) {
            setPendingData(data);
            setShowModal(true);
        } else {
            await proceedWithLogin(data);
        }
    };

    const proceedWithLogin = async (data: any) => {
        const response = await di.get<SignInUseCase>(SignInUseCaseName).call(data.email, data.password);

        if (isRight(response)) {
            if (!(response.right?.permissions?.employee != undefined)) {
                navigate(routes.rolesAndPermissions.relativePath);
            } else {
                navigate(routes.home.relativePath);
            }
        } else {
            if (response.left.code === KeyWordLocalization.AuthRepositoryErrorUserNotConfirmed)
                navigate(routes.confimr_account.relativePath + '/' + data.email);
            else if (response.left.code === KeyWordLocalization.AuthRepositoryErrorUserNeedPasswordChange)
                navigate(routes.reset_passowrd.relativePath + '/' + data.email);
            else
                addToast(i18n(response.left.code ?? KeyWordLocalization.SingInPageWrongCredentials), 'error', null, true);
        }
    };

    return (
        <div className='sign_in_page'>
            {showModal && (
                <div className="custom-modal-backdrop">
                    <div className="custom-modal">
                        <img
                            src={disclamerImage}
                            alt="Modal Notice"
                            className="modal-image"
                        />
                        <button className="btn btn_primary" onClick={handleModalAccept}>
                            Accept
                        </button>
                    </div>
                </div>
            )}

            <h5 className='text-center'>{i18n(KeyWordLocalization.SignInPageTitle)}</h5>
            <form ref={formRef} className="row" onSubmit={handleSubmit(onSubmit)}>
                <div className={`col-12 mb-3 form-group ${errors.email ? 'error' : ''}`}>
                    <label>{i18n(KeyWordLocalization.SignInPageEmail)}</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={i18n(KeyWordLocalization.SignInPageEmail)}
                        {...register('email', Validators({ required: true, email: true }))}
                    />
                    <ErrorMessage as="aside" errors={errors} name="email" />
                </div>

                <div className={`col-12 mb-3 form-group ${errors.password ? 'error' : ''}`}>
                    <label>{i18n(KeyWordLocalization.SignInPagePassword)}</label>
                    <div className="password_input_wrapper">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            className="form-control"
                            placeholder={i18n(KeyWordLocalization.SignInPagePassword)}
                            {...register('password', Validators({ required: true }))}
                        />
                        <div className="visibility_icon" onClick={() => setShowPassword(!showPassword)}>
                            {showPassword ? <MdVisibility /> : <MdVisibilityOff />}
                        </div>
                    </div>
                    <ErrorMessage as="aside" errors={errors} name="password" />
                </div>

                <Link className="forget_password_link mt-0 small" to={routes.send_code_recovery.relativePath}>
                    {i18n(KeyWordLocalization.SignInPageDoYouForgetPassword)}
                </Link>

                <button type="submit" className="btn btn_primary mt-4">
                    {i18n(KeyWordLocalization.SignInPageSubmit)}
                </button>
            </form>

            <SocialLoginComponent />
        </div>
    );
};

export default SignInPage;
